import { useEffect, useRef, useCallback } from 'react';

export interface UsePdfPanOptions {
    scrollRef: React.RefObject<HTMLDivElement | null>;
    handMode: boolean;
}

export function usePdfPan({ scrollRef, handMode }: UsePdfPanOptions) {
    const panRef = useRef<{ startX: number; startY: number; scrollLeft: number; scrollTop: number } | null>(null);

    const handlePanStart = useCallback((e: React.MouseEvent) => {
        if (!handMode) return;
        const el = scrollRef.current;
        if (!el) return;
        e.preventDefault();
        panRef.current = { startX: e.clientX, startY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
    }, [handMode, scrollRef]);

    const handlePanMove = useCallback((e: React.MouseEvent) => {
        if (!panRef.current) return;
        const el = scrollRef.current;
        if (!el) return;
        const dx = e.clientX - panRef.current.startX;
        const dy = e.clientY - panRef.current.startY;
        el.scrollLeft = panRef.current.scrollLeft - dx;
        el.scrollTop = panRef.current.scrollTop - dy;
    }, [scrollRef]);

    const handlePanEnd = useCallback(() => {
        panRef.current = null;
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onDown = (e: MouseEvent) => {
            if (e.button !== 1) return;
            e.preventDefault();
            panRef.current = { startX: e.clientX, startY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
            el.style.cursor = 'grabbing';
        };
        const onUp = (e: MouseEvent) => {
            if (e.button !== 1) return;
            panRef.current = null;
            el.style.cursor = '';
        };
        el.addEventListener('mousedown', onDown);
        el.addEventListener('mouseup', onUp);
        return () => {
            el.removeEventListener('mousedown', onDown);
            el.removeEventListener('mouseup', onUp);
        };
    }, [scrollRef]);

    return { panRef, handlePanStart, handlePanMove, handlePanEnd };
}
